import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  CircularProgress, 
  Alert, 
  Paper,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import TableViewIcon from '@mui/icons-material/TableView';
import SearchIcon from '@mui/icons-material/Search';
import StorageIcon from '@mui/icons-material/Storage';
import CodeIcon from '@mui/icons-material/Code';
import LanguageIcon from '@mui/icons-material/Language';
import SyncIcon from '@mui/icons-material/Sync';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ApiIcon from '@mui/icons-material/Api';
import { getAis, fetchTableSchema, createTable, listTables, deleteTable, syncTableData, getTableData, updateTable, uploadCsvPreview } from '../services/api';
import config from '../config';

// 🚨 IMMEDIATE DEBUG: This should appear as soon as the file is imported
console.log('🚨 DEBUG: Tables.tsx file is being imported/loaded!');
console.log('🚨 DEBUG: Current timestamp:', new Date().toISOString());


// TabPanel component to render content for each tab
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`table-tabpanel-${index}`}
      aria-labelledby={`table-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface Collection {
  _id: string;
  aiId: string;
  collectionName: string;
  version: string;
  isPublic: boolean;
}

interface AI {
  id: string;
  title: string;
  collections: Collection[];
}

interface DatabaseTable {
  id: string;
  name: string;
  schema: any;
  source: string;
  description: string;
  createdAt: string;
  updatedAt?: string;
  sourceType?: 'woocommerce' | 'csv-upload';
  originalFileName?: string;
  rowCount?: number;
  supportsVariations?: boolean;
}

// Interface for table data
// interface TableDataResponse {
//   data: any[];
//   total: number;
// }
const Tables = () => {
  const { aiId } = useParams<{ aiId: string }>();
  const [searchParams] = useSearchParams();
  const collectionId = searchParams.get('collectionId');
  const navigate = useNavigate();
  
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiDetails, setAiDetails] = useState<AI | null>(null);
  const [collection, setCollection] = useState<Collection | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Tables state
  const [tables, setTables] = useState<DatabaseTable[]>([]);
  const [apiUrl, setApiUrl] = useState('https://aixue.us/wp-json/wc/v3');
  const [apiKey, setApiKey] = useState('ck_ec74138c048a4b1ad3bc4ede2addbdadd895e701');
  const [apiSecret, setApiSecret] = useState('cs_0631739534346373fa4c14cf6405bb3d40a17ee6');
  const [tableName, setTableName] = useState('');
  const [tableDescription, setTableDescription] = useState('');
  const [addingTable, setAddingTable] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentTable, setCurrentTable] = useState<DatabaseTable | null>(null);
  const [tableSchema, setTableSchema] = useState<any>({});
  const [fetchingSchema, setFetchingSchema] = useState(false);
  const [syncingTable, setSyncingTable] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // CSV Upload state
  const [sourceType, setSourceType] = useState<'woocommerce' | 'csv-upload'>('woocommerce');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<any>(null);
  const [csvColumns, setCsvColumns] = useState<any[]>([]);
  // Removed csvData as it's not used - data comes from API responses
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [showCsvPreview, setShowCsvPreview] = useState(false);

  // Debug logging
  console.log('🔧 DEBUG: Tables.tsx state:', {
    sourceType,
    tabValue,
    csvFile: csvFile?.name,
    showCsvPreview,
    csvColumns: csvColumns.length
  });
  // Debug MenuItem rendering
  console.log('🔧 DEBUG: MenuItem components should render with options:', {
    woocommerce: 'WooCommerce API', 
    csvUpload: 'CSV File Upload'
  });
  
  // Table data and pagination state
  const [tableDataDialog, setTableDataDialog] = useState(false);
  const [tableData, setTableData] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loadingTableData, setLoadingTableData] = useState(false);

  // New state for custom SQL search
  const [customSql, setCustomSql] = useState('');
  const [realSql, setRealSql] = useState('');
  const [isCustomSqlMode, setIsCustomSqlMode] = useState(false);

  // New state for table description editing
  const [editDescriptionDialog, setEditDescriptionDialog] = useState(false);
  const [editingTable, setEditingTable] = useState<DatabaseTable | null>(null);
  const [newDescription, setNewDescription] = useState('');
  
  useEffect(() => {
    if (aiId && collectionId) {
      setInitialLoading(true);
      setError(null);
      
      // First, get AI details and find the collection details
      getAis()
        .then((res) => {
          const currentAi = res.data.find((ai: AI) => ai.id === aiId);
          setAiDetails(currentAi || null);
          
          if (currentAi) {
            const currentCollection = currentAi.collections.find(
              (c: Collection) => c._id === collectionId
            );
            setCollection(currentCollection || null);
          }
          
          // Fetch tables for this AI from the backend
          return listTables(aiId);
        })
        .then((res) => {
          if (res && res.data) {
            console.log('🔍 DEBUG: Tables API response:', res.data);
            console.log('🔍 DEBUG: First table object:', res.data[0]);
            console.log('🔍 DEBUG: Table sourceTypes:', res.data.map((t: DatabaseTable) => ({ name: t.name, sourceType: t.sourceType, originalFileName: t.originalFileName })));
            setTables(res.data);
          }
          setInitialLoading(false);
        })
        .catch((err) => {
          console.error('Error in Tables component:', err);
          
          // If we get a 401 Unauthorized error, redirect to login
          if (err.response && err.response.status === 401) {
            setError(null);
            navigate('/login', { replace: true });
          } else {
            setError('Error loading tables data: ' + (err.response?.data?.error || err.message));
          }
          
          setInitialLoading(false);
        });
    }
  }, [aiId, collectionId, navigate]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAddTable = () => {
    // Validate inputs
    if (!tableName || !apiUrl || !apiKey || !apiSecret || Object.keys(tableSchema).length === 0) {
      setError('Please provide all required fields and fetch the schema first');
      return;
    }
    
    setAddingTable(true);
    setError(null);
    
    // Create the source config object with API credentials
    const sourceConfig = {
      apiUrl: apiUrl,
      apiKey: apiKey,
      apiSecret: apiSecret
    };
    
    // Call the API to create a new table in Supabase
    createTable(aiId as string, {
      name: tableName,
      description: tableDescription || '',
      source: apiUrl,
      sourceConfig: sourceConfig,
      schema: tableSchema,
      collectionId: searchParams.get('collectionId')
    })
      .then(async (res) => {
        console.log('Table created:', res.data);
        
        // Reset form fields
        setTableName('');
        setTableDescription('');
        setApiUrl('');
        setApiKey('');
        setApiSecret('');
        setTableSchema({});
        
        // Show success message
        setSuccessMessage('Table created successfully');
        
        // Switch to the Existing Tables tab
        setTabValue(0);
        
        // Refresh tables list
        const tablesResponse = await listTables(aiId!);
        if (tablesResponse && tablesResponse.data) {
          console.log('🔍 DEBUG: Tables refreshed after table creation:', tablesResponse.data);
          console.log('🔍 DEBUG: New table sourceTypes after creation:', tablesResponse.data.map((t: DatabaseTable) => ({ name: t.name, sourceType: t.sourceType, originalFileName: t.originalFileName })));
          setTables(tablesResponse.data);
        }
      })
      .catch((err) => {
        console.error('Error creating table:', err);
        setError('Failed to create table: ' + (err.response?.data?.error || err.message));
      })
      .finally(() => {
        setAddingTable(false);
      });
  };

  const handleFetchSchema = () => {
    // Validate inputs
    if (!apiUrl || !apiKey || !apiSecret || !tableName) {
      setError('Please provide table name, API URL, key, and secret');
      return;
    }

    const collectionId = searchParams.get('collectionId') || '';

    setFetchingSchema(true);
    setError(null);
    
    // Call the API to fetch schema from WooCommerce with table types array
    fetchTableSchema(aiId as string, { 
      apiUrl, 
      apiKey, 
      apiSecret,
      tableTypes: [tableName], // Pass as array since API now expects multiple tables
      collectionId
    })
      .then((res) => {
        // Check if the response includes a created table
        if (res.data.table) {
          // Table was automatically created
          console.log('Table automatically created:', res.data.table);
          
          // Add the new table to the tables list
          setTables([...tables, res.data.table]);
          
          // Show success message
          setSuccessMessage(res.data.message || 'Table created successfully');
          
          // Reset form fields
          setTableName('');
          setTableDescription('');
          setApiUrl('');
          setApiKey('');
          setApiSecret('');
          setTableSchema({});
          
          // Switch to the Existing Tables tab
          setTabValue(0);
        } else {
          // Only schema was returned
          console.log('Fetched schema:', res.data.schema);
          setTableSchema(res.data.schema);
        }
      })
      .catch((err) => {
        console.error('Error fetching schema:', err);
        setError('Failed to fetch schema: ' + (err.response?.data?.error || err.message));
      })
      .finally(() => {
        setFetchingSchema(false);
      });
  };
  
  const handleDeleteTable = (table: DatabaseTable) => {
    setCurrentTable(table);
    setOpenDialog(true);
  };
  
  const confirmDelete = () => {
    if (currentTable) {
      setError(null);
      
      // Get the table ID, preferring id but falling back to _id if id is undefined
      const tableId = currentTable.id || (currentTable as any)._id;
      
      if (!tableId) {
        setError('Table ID is missing. Please try refreshing the page.');
        return;
      }
      
      // Call the API to delete the table
      deleteTable(aiId as string, tableId)
        .then(() => {
          // Remove the deleted table from the state
          setTables(tables.filter(t => (t.id !== tableId && (t as any)._id !== tableId)));
          
          // Show success message
          setSuccessMessage(`Table "${currentTable.name}" deleted successfully`);
          
          // Close the dialog
          setOpenDialog(false);
          setCurrentTable(null);
        })
        .catch((err) => {
          console.error('Error deleting table:', err);
          setError('Failed to delete table: ' + (err.response?.data?.error || err.message));
          setOpenDialog(false); // Close the dialog anyway
        });
    }
  };
  
  const handleViewTable = (table: DatabaseTable) => {
    // Set the current table and reset pagination
    setCurrentTable(table);
    setPage(0);
    setTableData([]);
    setTotalRows(0);
    setLoadingTableData(false);
    setError(null);
    setIsCustomSqlMode(false);
    setCustomSql('');
    setRealSql('');
    
    // Just open the dialog without fetching data
    setTableDataDialog(true);
  };

  const handleSyncTable = (table: DatabaseTable) => {
    // Get the table ID, preferring id but falling back to _id if id is undefined
    const tableId = table.id || (table as any)._id;
    
    if (!tableId) {
      setError('Table ID is missing. Please try refreshing the page.');
      return;
    }
    
    setSyncingTable(tableId);
    setError(null);
    
    syncTableData(aiId as string, tableId)
      .then((res) => {
        console.log('Sync response:', res.data);
        
        // Update the table in the state with new updatedAt time
        setTables(tables.map(t => 
          (t.id === tableId || (t as any)._id === tableId)
            ? { ...t, ...res.data.table } 
            : t
        ));
        
        // Show success message
        setSuccessMessage(res.data.message || `Table "${table.name}" synced successfully`);
      })
      .catch((err) => {
        console.error('Error syncing table:', err);
        setError('Failed to sync table: ' + (err.response?.data?.error || err.message));
      })
      .finally(() => {
        setSyncingTable(null);
      });
  };

  // CSV Upload handlers
  const handleCsvFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCsvFile(file);
      setShowCsvPreview(false);
      setCsvPreview(null);
      setCsvColumns([]);
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile || !aiId) return;

    setUploadingCsv(true);
    setError(null);

    try {
      const response = await uploadCsvPreview(aiId, csvFile);
      const { preview, schema } = response.data;
      
      setCsvPreview(preview);
      setCsvColumns(preview.columns);
      setTableSchema(schema);
      setShowCsvPreview(true);
      setTableName(preview.originalFileName.replace('.csv', ''));
    } catch (err: any) {
      console.error('Error uploading CSV:', err);
      setError('Failed to upload CSV: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploadingCsv(false);
    }
  };

  const handleUpdateColumnName = (index: number, newName: string) => {
    const updatedColumns = [...csvColumns];
    updatedColumns[index].name = newName.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    setCsvColumns(updatedColumns);
  };

  const handleUpdateColumnType = (index: number, newType: string) => {
    const updatedColumns = [...csvColumns];
    updatedColumns[index].type = newType;
    setCsvColumns(updatedColumns);
  };

  // Helper function to get source type badge
  const getSourceBadge = (table: DatabaseTable) => {
    const sourceType = table.sourceType || 'woocommerce';
    const originalFileName = table.originalFileName;
    
    console.log('🎯 DEBUG: getSourceBadge called for table:', {
      name: table.name,
      sourceType,
      originalFileName,
      fullTable: table
    });
    
    if (sourceType === 'csv-upload') {
      return (
        <Chip 
          icon={<UploadFileIcon />}
          label={`CSV Upload: ${originalFileName || table.source}`} 
          color="success" 
          size="small" 
          variant="outlined"
          sx={{ mr: 1, fontWeight: 500 }}
        />
      );
    } else {
      // WooCommerce or other API source
      const domain = table.source.includes('://') 
        ? new URL(table.source).hostname 
        : table.source;
      return (
        <Chip 
          icon={<ApiIcon />}
          label={`WooCommerce API: ${domain}`} 
          color="primary" 
          size="small" 
          variant="outlined"
          sx={{ mr: 1, fontWeight: 500 }}
        />
      );
    }
  };

  // Helper function to get the appropriate icon for table source
  const getSourceIcon = (table: DatabaseTable) => {
    const sourceType = table.sourceType || 'woocommerce';
    
    if (sourceType === 'csv-upload') {
      return <UploadFileIcon color="success" />;
    } else {
      return <StorageIcon color="primary" />;
    }
  };

  // Helper function to get source-specific secondary text
  const getSourceSecondaryText = (table: DatabaseTable) => {
    const sourceType = table.sourceType || 'woocommerce';
    
    if (sourceType === 'csv-upload') {
      return (
        <>
          <Typography variant="body2" component="span" color="text.secondary">
            Static data from CSV file • No sync required
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            Uploaded: {new Date(table.createdAt).toLocaleString()}
          </Typography>
        </>
      );
    } else {
      return (
        <>
          <Typography variant="body2" component="span" color="text.secondary">
            Live data from API • Supports sync
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            Created: {new Date(table.createdAt).toLocaleString()}
            {table.updatedAt && (
              <>
                <br />
                Last Synced: {new Date(table.updatedAt).toLocaleString()}
              </>
            )}
          </Typography>
        </>
      );
    }
  };

  const handleCreateCsvTable = async () => {
    if (!csvFile || !csvColumns.length || !aiId) return;

    setAddingTable(true);
    setError(null);

    try {
      // Process CSV file with updated column definitions
      const csvProcessResponse = await uploadCsvPreview(aiId, csvFile);
      const fullCsvData = csvProcessResponse.data.preview.previewRows; // This should be all rows, not just preview

      // Create table with CSV data
      const tableData = {
        name: tableName,
        description: tableDescription,
        source: csvFile.name,
        sourceType: 'csv-upload',
        originalFileName: csvFile.name,
        sourceConfig: { 
          fileName: csvFile.name,
          columnMapping: csvColumns 
        },
        schema: tableSchema,
        csvData: fullCsvData,
        collectionId: searchParams.get('collectionId')
      };

      await createTable(aiId, tableData);
      
      setSuccessMessage(`CSV table "${tableName}" created successfully`);
      
      // Reset form
      setSourceType('woocommerce');
      setCsvFile(null);
      setCsvPreview(null);
      setCsvColumns([]);
      setShowCsvPreview(false);
      setTableName('');
      setTableDescription('');
      setTabValue(0); // Switch back to existing tables tab
      
      // Refresh tables list
      const tablesResponse = await listTables(aiId!);
      if (tablesResponse && tablesResponse.data) {
        console.log('🔍 DEBUG: Tables refreshed after CSV creation:', tablesResponse.data);
        console.log('🔍 DEBUG: CSV table sourceTypes after creation:', tablesResponse.data.map((t: DatabaseTable) => ({ name: t.name, sourceType: t.sourceType, originalFileName: t.originalFileName })));
        setTables(tablesResponse.data);
      }
    } catch (err: any) {
      console.error('Error creating CSV table:', err);
      setError('Failed to create CSV table: ' + (err.response?.data?.error || err.message));
    } finally {
      setAddingTable(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    setLoadingTableData(true);
    
    if (isCustomSqlMode && realSql) {
      // When in custom SQL mode, use the cached SQL
      // Get the table ID, preferring id but falling back to _id if id is undefined
      const tableId = currentTable?.id || (currentTable as any)?._id;
      
      if (!tableId) {
        setError('Table ID is missing. Please try refreshing the page.');
        setLoadingTableData(false);
        return;
      }
      
      fetch(`${config.apiBaseUrl}/trainer/ais/${aiId}/tables/${tableId}/sql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          sqlraw: customSql, 
          page: newPage + 1, 
          pageSize: rowsPerPage,
          cachedSql: realSql // Use the cached SQL
        })
      })
        .then(response => {
          if (!response.ok) {
            return response.json().then(err => {
              throw new Error(err.error || 'Failed to execute SQL query');
            });
          }
          return response.json();
        })
        .then(data => {
          setTableData(data.data || []);
          setTotalRows(data.pagination?.totalRows || data.data?.length || 0);
        })
        .catch(err => {
          console.error('Error executing cached SQL:', err);
          setError('Failed to fetch data: ' + err.message);
        })
        .finally(() => {
          setLoadingTableData(false);
        });
    } else {
      // Normal table data view (not custom SQL)
      // Get the table ID, preferring id but falling back to _id if id is undefined
      const tableId = currentTable?.id || (currentTable as any)?._id;
      
      if (!tableId) {
        setError('Table ID is missing. Please try refreshing the page.');
        setLoadingTableData(false);
        return;
      }
      
      getTableData(aiId as string, tableId, newPage + 1, rowsPerPage)
        .then((res) => {
          setTableData(res.data.data);
          setTotalRows(res.data.pagination?.totalRows || res.data.data?.length || 0);
        })
        .catch((err) => {
          console.error('Error fetching table data:', err);
          setError('Failed to fetch table data: ' + (err.response?.data?.error || err.message));
        })
        .finally(() => {
          setLoadingTableData(false);
        });
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setLoadingTableData(true);
    
    // Fetch the data with the new page size
    getTableData(aiId as string, currentTable?.id as string, 1, newRowsPerPage)
      .then((res) => {
        setTableData(res.data.data);
        setTotalRows(res.data.pagination?.totalRows || res.data.data?.length || 0);
      })
      .catch((err) => {
        console.error('Error fetching table data:', err);
        setError('Failed to fetch table data: ' + (err.response?.data?.error || err.message));
      })
      .finally(() => {
        setLoadingTableData(false);
      });
  };

  // New function to handle running custom SQL queries
  const handleRunSql = () => {
    if (!currentTable) return;
    if (!customSql.trim()) return;
    
    // Get the table ID, preferring id but falling back to _id if id is undefined
    const tableId = currentTable.id || (currentTable as any)._id;
    
    if (!tableId) {
      setError('Table ID is missing. Please try refreshing the page.');
      return;
    }
    
    setLoadingTableData(true);
    setError(null);
    setIsCustomSqlMode(true);
    
    // Call backend API to execute the SQL query
    fetch(`${config.apiBaseUrl}/trainer/ais/${aiId}/tables/${tableId}/sql`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ 
        sqlraw: customSql, 
        page: page + 1, 
        pageSize: rowsPerPage 
      })
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            throw new Error(err.error || 'Failed to execute SQL query');
          });
        }
        return response.json();
      })
      .then(data => {
        if (data.cachedSql) {
          setRealSql(data.cachedSql); // Store the cached SQL for reuse
        }
        setTableData(data.data || []);
        setTotalRows(data.pagination?.totalRows || data.data?.length || 0);
      })
      .catch(err => {
        console.error('Error executing SQL:', err);
        setError('Failed to execute SQL query: ' + err.message);
        setIsCustomSqlMode(false);
      })
      .finally(() => {
        setLoadingTableData(false);
      });
  };
  
  // Reset SQL mode when dialog closes
  const handleCloseDataDialog = () => {
    setTableDataDialog(false);
    setIsCustomSqlMode(false);
    setCustomSql('');
    setRealSql('');
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* 🚨 ULTIMATE DEBUG: This should be visible if Tables.tsx is loading */}
      <Box sx={{ 
        bgcolor: 'red', 
        color: 'white', 
        p: 3, 
        textAlign: 'center', 
        fontSize: '24px',
        fontWeight: 'bold',
        border: '5px solid yellow',
        margin: 2
      }}>
        🚨 DEBUG v3.0: Tables.tsx Component IS DEFINITELY LOADING! 🚨
        <br />
        Time: {new Date().toLocaleTimeString()}
        <br />
        SourceType: {sourceType} | TabValue: {tabValue}
      </Box>

      <Typography variant="h4" gutterBottom>
        Databases {aiDetails ? `for ${aiDetails.title}` : ''}
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Success message snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={successMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
      
      {collection && (
        <>
          <Box sx={{ 
            p: 2, 
            mb: 3, 
            bgcolor: '#f5f5f5', 
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box>
              <Typography variant="h6">
                Version: {collection.version}
              </Typography>
              {/* <Typography variant="body2" color="text.secondary">
                Collection ID: {collection._id}
              </Typography> */}
            </Box>
            
            <Box>
              <Typography variant="body2" color="text.secondary">
                {collection.isPublic ? "Public Collection" : "Private Collection"}
              </Typography>
            </Box>
          </Box>
          
          <Paper sx={{ mb: 4 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
              aria-label="Database management tabs"
            >
              <Tab icon={<TableViewIcon />} label="Existing Databases" id="db-tab-0" aria-controls="db-tabpanel-0" />
              <Tab icon={<AddIcon />} label="Add New Database" id="db-tab-1" aria-controls="db-tabpanel-1" />
            </Tabs>
            
            {/* Existing Databases Tab */}
            <TabPanel value={tabValue} index={0}>
              {tables.length > 0 ? (
                <List>
                  {tables.map((table) => (
                    <React.Fragment key={table.id}>
                      <ListItem>
                        <ListItemIcon>
                          {getSourceIcon(table)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="h6" component="span">
                                {table.name}
                              </Typography>
                              {getSourceBadge(table)}
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography variant="body2" component="span" sx={{ mb: 1, display: 'block' }}>
                                Description: {table.description || 'No description provided'}
                              </Typography>
                              {getSourceSecondaryText(table)}
                            </>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="Edit Description">
                            <IconButton 
                              edge="end" 
                              aria-label="edit" 
                              onClick={() => {
                                setEditingTable(table);
                                setNewDescription(table.description);
                                setEditDescriptionDialog(true);
                              }}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          {/* Only show sync button for WooCommerce tables */}
                          {(table.sourceType || 'woocommerce') === 'woocommerce' && (
                            <Tooltip title="Sync Latest Data from WooCommerce API">
                              <IconButton 
                                edge="end" 
                                aria-label="sync" 
                                onClick={() => handleSyncTable(table)}
                                disabled={syncingTable === table.id}
                                color="primary"
                              >
                                {syncingTable === table.id ? <CircularProgress size={24} /> : <SyncIcon />}
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Query Data">
                            <IconButton color="primary" edge="end" aria-label="view" onClick={() => handleViewTable(table)}>
                              <SearchIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Database">
                            <IconButton 
                              edge="end" 
                              aria-label="delete" 
                              onClick={() => handleDeleteTable(table)}
                              disabled={syncingTable === table.id}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider variant="inset" component="li" />
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No databases have been added yet.
                  </Typography>
                  <Button 
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    sx={{ mt: 2 }}
                    onClick={() => setTabValue(1)}
                  >
                    Add a New Database
                  </Button>
                </Box>
              )}
            </TabPanel>
            
            {/* Add New Database Tab */}
            <TabPanel value={tabValue} index={1}>
              {/* Debug: Tab Panel Loading */}
              <Box sx={{ bgcolor: 'orange', color: 'black', p: 2, mb: 2, border: '3px solid blue' }}>
                🚨 DEBUG: Add New Database TabPanel is RENDERING! 
                <br />TabValue: {tabValue} (should be 1 when this tab is active)
              </Box>
              
              <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                🔧 DEBUG VERSION: CSV Upload Feature v1.0.0
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Connect to a new database source. Supports WooCommerce APIs and CSV file uploads.
              </Typography>
              
              <Box sx={{ mt: 3 }}>
                {/* Debug: Before Dropdown */}
                <Box sx={{ bgcolor: 'lime', color: 'black', p: 1, mb: 2, border: '2px solid green' }}>
                  🚨 DEBUG: About to render dropdown! SourceType = {sourceType}
                </Box>
                
                {/* Source Type Selection */}
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Data Source</InputLabel>
                  <Select
                    value={sourceType}
                    label="Data Source"
                    onChange={(e) => {
                      console.log('🔧 DEBUG: Data Source changed to:', e.target.value);
                      setSourceType(e.target.value as 'woocommerce' | 'csv-upload');
                      setError(null);
                      setShowCsvPreview(false);
                    }}
                    disabled={addingTable || uploadingCsv}
                  >
                    <MenuItem value="woocommerce">🔧 WooCommerce API</MenuItem>
                    <MenuItem value="csv-upload">🔧 CSV File Upload</MenuItem>
                  </Select>
                  
                  {/* Debug: After Dropdown */}
                  <Box sx={{ bgcolor: 'cyan', color: 'black', p: 1, mt: 1, border: '2px solid teal' }}>
                    🚨 DEBUG: Dropdown rendered! Should have 2 options above.
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Choose your data source type
                  </Typography>
                </FormControl>

                {/* Conditional UI based on source type */}
                <Typography variant="body2" sx={{ mt: 2, p: 1, bgcolor: 'yellow', color: 'black' }}>
                  🔧 DEBUG: Current sourceType = "{sourceType}"
                </Typography>
                
                {sourceType === 'woocommerce' ? (
                  // WooCommerce form
                  <>
                    <Typography variant="body2" sx={{ bgcolor: 'lightblue', p: 1, mt: 1 }}>
                      🔧 DEBUG: Rendering WooCommerce form
                    </Typography>
                    <FormControl fullWidth margin="normal" required>
                      <InputLabel>Table Type</InputLabel>
                      <Select
                        value={tableName}
                        label="Table Type"
                        onChange={(e) => setTableName(e.target.value)}
                        disabled={addingTable}
                      >
                        <MenuItem value="Products">Products</MenuItem>
                        <MenuItem value="Orders">Orders</MenuItem>
                        <MenuItem value="Customers">Customers</MenuItem>
                        <MenuItem value="Coupons">Coupons</MenuItem>
                      </Select>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        Select the WooCommerce table type to import
                      </Typography>
                    </FormControl>
                  </>
                ) : (
                  // CSV Upload form
                  <>
                    <Typography variant="body2" sx={{ bgcolor: 'lightgreen', p: 1, mt: 1 }}>
                      🔧 DEBUG: Rendering CSV Upload form
                    </Typography>
                    <TextField
                      label="Table Name"
                      fullWidth
                      margin="normal"
                      value={tableName}
                      onChange={(e) => setTableName(e.target.value)}
                      disabled={addingTable || uploadingCsv}
                      required
                      helperText="Name for your database table"
                    />
                  </>
                )}
                
                <TextField
                  label="Table Description"
                  fullWidth
                  margin="normal"
                  value={tableDescription}
                  onChange={(e) => setTableDescription(e.target.value)}
                  disabled={addingTable || uploadingCsv}
                  multiline
                  rows={2}
                />

                {/* CSV Upload Section */}
                {sourceType === 'csv-upload' && (
                  <>
                    <Box sx={{ mt: 3, mb: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        CSV File Upload
                      </Typography>
                      <Divider />
                    </Box>

                    <Box sx={{ mt: 2, mb: 2 }}>
                      <input
                        accept=".csv"
                        style={{ display: 'none' }}
                        id="csv-upload-button"
                        type="file"
                        onChange={handleCsvFileChange}
                        disabled={addingTable || uploadingCsv}
                      />
                      <label htmlFor="csv-upload-button">
                        <Button
                          variant="outlined"
                          component="span"
                          disabled={addingTable || uploadingCsv}
                          startIcon={<StorageIcon />}
                        >
                          Choose CSV File
                        </Button>
                      </label>
                      {csvFile && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Selected: {csvFile.name}
                        </Typography>
                      )}
                    </Box>

                    {csvFile && !showCsvPreview && (
                      <Button
                        variant="contained"
                        onClick={handleCsvUpload}
                        disabled={uploadingCsv}
                        startIcon={uploadingCsv ? <CircularProgress size={20} /> : <StorageIcon />}
                        sx={{ mb: 2 }}
                      >
                        {uploadingCsv ? 'Processing...' : 'Preview CSV'}
                      </Button>
                    )}
                  </>
                )}
                
                {/* WooCommerce Configuration - only show for WooCommerce source */}
                {sourceType === 'woocommerce' && (
                  <>
                    <Box sx={{ mt: 3, mb: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        WooCommerce API Configuration
                      </Typography>
                      <Divider />
                    </Box>
                    
                    <TextField
                  label="API URL"
                  placeholder="https://yourstore.com/wp-json/wc/v3"
                  fullWidth
                  margin="normal"
                  value={apiUrl}
                  onChange={(e) => setApiUrl(e.target.value)}
                  disabled={addingTable || fetchingSchema}
                  required
                  helperText="For WooCommerce, use: https://yourstore.com/wp-json/wc/v3"
                  InputProps={{
                    startAdornment: <LanguageIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="API Key"
                    fullWidth
                    margin="normal"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    disabled={addingTable || fetchingSchema}
                    required
                  />
                  
                  <TextField
                    label="API Secret"
                    fullWidth
                    margin="normal"
                    value={apiSecret}
                    onChange={(e) => setApiSecret(e.target.value)}
                    disabled={addingTable || fetchingSchema}
                    required
                    type="password"
                  />
                </Box>
                
                <Box sx={{ mt: 2, mb: 4 }}>
                  <Button
                    variant="outlined"
                    onClick={handleFetchSchema}
                    disabled={!apiUrl || !apiKey || !apiSecret || addingTable || fetchingSchema}
                    startIcon={fetchingSchema ? <CircularProgress size={20} /> : <CodeIcon />}
                  >
                    {fetchingSchema ? 'Copying Database...' : 'Copy Database'}
                  </Button>
                </Box>
                  </>
                )}

                {/* CSV Preview Section */}
                {sourceType === 'csv-upload' && showCsvPreview && csvPreview && (
                  <>
                    <Box sx={{ mt: 3, mb: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        CSV Preview & Column Configuration
                      </Typography>
                      <Divider />
                    </Box>
                    
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Found {csvPreview.totalRows} rows with {csvPreview.columns.length} columns. 
                      Review and adjust column names/types below, then create the table.
                    </Alert>
                    
                    <TableContainer component={Paper} sx={{ mb: 3, maxHeight: 400 }}>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Column Name</TableCell>
                            <TableCell>Data Type</TableCell>
                            <TableCell>Sample Values</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {csvColumns.map((column, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <TextField
                                  size="small"
                                  value={column.name}
                                  onChange={(e) => handleUpdateColumnName(index, e.target.value)}
                                  placeholder="Column name"
                                  sx={{ minWidth: 120 }}
                                />
                              </TableCell>
                              <TableCell>
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                  <Select
                                    value={column.type}
                                    onChange={(e) => handleUpdateColumnType(index, e.target.value)}
                                  >
                                    <MenuItem value="text">Text</MenuItem>
                                    <MenuItem value="integer">Integer</MenuItem>
                                    <MenuItem value="real">Decimal</MenuItem>
                                    <MenuItem value="boolean">Boolean</MenuItem>
                                    <MenuItem value="date">Date</MenuItem>
                                  </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" sx={{ display: 'block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {column.sampleValues.slice(0, 3).join(', ')}
                                  {column.sampleValues.length > 3 && '...'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <Button
                      variant="contained"
                      onClick={handleCreateCsvTable}
                      disabled={addingTable || !tableName}
                      startIcon={addingTable ? <CircularProgress size={20} /> : <AddIcon />}
                      sx={{ mb: 2 }}
                    >
                      {addingTable ? 'Creating Table...' : 'Create CSV Table'}
                    </Button>
                  </>
                )}
                
                {/* WooCommerce Schema Display */}
                {sourceType === 'woocommerce' && Object.keys(tableSchema).length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Detected Schema
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                      <pre style={{ margin: 0, overflow: 'auto' }}>
                        {JSON.stringify(tableSchema, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                )}
                
                {/* WooCommerce Create Table Button */}
                {sourceType === 'woocommerce' && Object.keys(tableSchema).length > 0 && (
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={handleAddTable}
                      disabled={
                        !tableName || 
                        !apiUrl || 
                        !apiKey || 
                        !apiSecret || 
                        Object.keys(tableSchema).length === 0 || 
                        addingTable
                      }
                      startIcon={addingTable ? <CircularProgress size={20} /> : <AddIcon />}
                    >
                      {addingTable ? 'Creating Table...' : 'Create Table'}
                    </Button>
                  </Box>
                )}
              </Box>
            </TabPanel>
          </Paper>
        </>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      >
        <DialogTitle>Delete Database</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the database "{currentTable?.name}"? 
            This action cannot be undone and will permanently remove all data in this database.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Table Data Dialog */}
      <Dialog
        open={tableDataDialog}
        onClose={handleCloseDataDialog}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ pb: 1 }}>
          {currentTable?.name}
          {loadingTableData && <CircularProgress size={24} sx={{ ml: 2 }} />}
        </DialogTitle>
        
        {/* Product filtering information */}
        {currentTable?.name.toLowerCase().includes('product') && (
          <Alert severity="info" sx={{ mx: 3, mb: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> The AI will automatically filter out products that are hidden in the catalog, 
              have a private status, or are not purchasable (sold out). Only visible, public, and available 
              products will appear in query results.
            </Typography>
          </Alert>
        )}
        
        {/* User query input bar at the top */}
        <Box sx={{ px: 3, pb: 2, display: 'flex', gap: 1 }}>
          <TextField
            label="Input your query here"
            fullWidth
            value={customSql}
            onChange={(e) => setCustomSql(e.target.value)}
            disabled={loadingTableData}
            size="small"
            placeholder={`short description contains 数学`}
          />
          <Button
            variant="contained"
            onClick={handleRunSql}
            disabled={!customSql.trim() || loadingTableData}
            sx={{ minWidth: '100px' }}
          >
            Run
          </Button>
        </Box>

        {/* Translated SQL query */}
        {/* <Box sx={{ px: 3, pb: 2, display: 'flex', gap: 1 }}>
          <TextField
            label="SQL"
            fullWidth
            value={realSql}
            disabled={true}
            size="small"
            placeholder={`SQL query written by AI`}
          />
        </Box> */}
        
        <DialogContent sx={{ overflow: 'hidden', pt: 0 }}>
          {/* {isCustomSqlMode && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Showing results of custom SQL query
            </Alert>
          )} */}
          
          {tableData.length > 0 ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">
                  {/* Display total rows count */}
                  {`Showing ${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, totalRows)} of ${totalRows} records`}
                </Typography>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={totalRows || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} of ${count !== -1 ? count : 'more than ' + to}`
                  }
                />
              </Box>
              <TableContainer 
                component={Paper} 
                sx={{ 
                  height: 'calc(100vh - 300px)', // Calculate height based on viewport
                  maxHeight: '500px', // Set maximum height
                  overflowY: 'auto', // Enable vertical scrolling within the table only
                  overflowX: 'auto' // Keep horizontal scrolling
                }}
              >
                <Table stickyHeader aria-label="table data" size="small">
                  <TableHead>
                    <TableRow>
                      {/* Get column headers from the first row of data */}
                      {Object.keys(tableData[0]).map((column) => (
                        <TableCell key={column} sx={{minWidth: '150px'}}>

                          <Typography variant="subtitle2">{column}</Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData.map((row, rowIndex) => (
                      <TableRow 
                        key={rowIndex}
                        sx={{ maxHeight: '100px' }}
                      >
                        {Object.entries(row).map(([key, cell], cellIndex) => (
                          <TableCell 
                            key={`cell-${rowIndex}-${cellIndex}`}
                            sx={{
                              maxWidth: '250px', // Limit cell width
                              overflow: 'auto', // Add scrollbar for overflowing content
                              whiteSpace: 'normal', // Allow text wrapping
                              wordBreak: 'break-word' // Break long words
                            }}
                          >
                            {(() => {
                              const stringValue = typeof cell === 'object' 
                                ? JSON.stringify(cell) 
                                : String(cell);
                              
                              return stringValue.length > 200
                                ? `${stringValue.substring(0, 200000)}`
                                : stringValue;
                            })()}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : loadingTableData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Typography variant="body1" sx={{ py: 3, textAlign: 'center' }}>
              Please run a query to see results here.

            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDataDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Description Dialog */}
      <Dialog
        open={editDescriptionDialog}
        onClose={() => setEditDescriptionDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { 
            width: '100%',
            overflowX: 'hidden'
          }
        }}
      >
        <DialogTitle>Edit Database Description</DialogTitle>
        <DialogContent
          sx={{
            overflowX: 'hidden',
            pb: 2,
            '&::-webkit-scrollbar-horizontal': {
              display: 'none'
            },
            msOverflowStyle: 'none',  /* IE and Edge */
            scrollbarWidth: 'none'  /* Firefox */
          }}
        >
          <DialogContentText sx={{ mb: 2 }}>
            Update the description for table "{editingTable?.name}".
          </DialogContentText>
          
          {/* Database Instructions Guidance */}
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f3e5f5', borderRadius: 1, borderLeft: '4px solid #9c27b0' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>🗄️ Database Description Guide:</strong> This description helps the AI understand and use your database effectively. Include:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2, mb: 1 }}>
              • <strong>Purpose:</strong> What is this database/table for? (orders, customers, products, etc.)<br/>
              • <strong>Key Columns:</strong> List the most important columns and what they contain<br/>
              • <strong>Relationships:</strong> How does this table relate to others?<br/>
              • <strong>Data Types:</strong> Any special formats or constraints to be aware of
            </Typography>
            <Typography variant="body2" color="error.main" sx={{ fontWeight: 'bold' }}>
              ⚠️ <strong>Security & Privacy:</strong> Include any security warnings, sensitive data notices, or privacy constraints that the AI must respect.
            </Typography>
          </Box>
          
          <TextField
            autoFocus
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={8}
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            sx={{ 
              width: '100%',
              '& .MuiInputBase-root': {
                width: '100%'
              }
            }}
            inputProps={{
              style: {
                width: '100%'
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDescriptionDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              if (editingTable) {
                setError(null);
                
                // Get the table ID, preferring id but falling back to _id if id is undefined
                const tableId = editingTable.id || (editingTable as any)._id;
                
                if (!tableId) {
                  setError('Table ID is missing. Please try refreshing the page.');
                  return;
                }
                
                // Check console for debugging
                console.log('Updating table description for:', tableId, 'with:', newDescription);
                updateTable(aiId as string, tableId, { description: newDescription })
                  .then((res) => {
                    console.log('Update response:', res.data);
                    // Update the table in the state
                    setTables(tables.map(t => 
                      (t.id === tableId || (t as any)._id === tableId)
                        ? { ...t, description: newDescription } 
                        : t
                    ));
                    setSuccessMessage(`Description for "${editingTable.name}" updated successfully`);
                    setEditDescriptionDialog(false);
                  })
                  .catch((err) => {
                    console.error('Error updating table description:', err);
                    setError('Failed to update table description: ' + (err.response?.data?.error || err.message));
                  });
              }
            }}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tables;